import getSrcUrl from '../util/getSrcUrl';

/** What the `valueType` property may be set to. */
export const STORE_FILE_OBJECT = '_fileObject';
export const STORE_URL = '_url';

/**
 * The value a FileSelector writes to its binding path.
 *
 * Two properties decide it, and they are independent of each other:
 *
 * `valueType` picks the shape. `_fileObject` keeps whatever the selection
 * produced - browsing produces a URL string, an upload produces the whole
 * response (`{url, directory, name, size, ...}`) - so the shape follows the
 * design type. `_url` stores a URL string whichever way the file arrived, which
 * is what a reader of the binding usually wants: a favicon is
 * `properties.links.<key>.href`, a manifest icon is `src`, an image is `src`,
 * each of them a string. Before this was a property, a page that wanted the
 * string had to unwrap the object itself one step later; sitezump's Site
 * Settings carried exactly that workaround on save.
 *
 * `fullUrl` picks how the URL inside that shape reads: an absolute URL, or the
 * files-API path. A value that leaves the app - a PWA manifest entry, an email,
 * an API payload - is read by something with no idea what this app's origin is,
 * so it needs the absolute form. `getSrcUrl` gives the CDN host when one is
 * configured and returns its input untouched when none is, which makes the
 * page's own origin the floor.
 *
 * `_fileObject` with `fullUrl` on rewrites the object's `url` key and leaves
 * every other key alone, so the absolute form never costs you `name` or `size`.
 *
 * Pulled out of the component so it can be tested: this is the shape contract
 * between the file browser and every page that binds one.
 */
export function fileSelectorStoredValue(
	value: any,
	valueType: string,
	fullUrl: boolean,
	origin: string,
): any {
	if (!value) return value;

	const keepObject = valueType !== STORE_URL;
	// Nothing to do to a string we are not making absolute, and nothing to do to
	// an object we are neither making absolute nor flattening.
	if (!fullUrl && keepObject) return value;

	const raw = typeof value === 'string' ? value : (value?.url ?? '');
	// Not a file-shaped value at all. Handing it back untouched is safer than
	// replacing it with an empty string, which would silently clear a binding.
	if (!raw) return value;

	if (!fullUrl) return raw;

	// transform off: this value is the file, not a rendering of it. It goes into
	// a binding, a manifest or an API payload, where naming a resized derivative
	// would hand the reader a thumbnail in place of the original.
	const cdn = getSrcUrl(raw, { transform: false });
	let absolute = cdn;
	if (!/^[a-z]+:\/\//i.test(cdn)) {
		absolute = cdn.startsWith('/') ? origin + cdn : origin + '/' + cdn;
	}

	if (keepObject && typeof value === 'object') return { ...value, url: absolute };
	return absolute;
}

/**
 * The request that deletes the file a FileSelector holds.
 *
 * The value is a file url as the files service hands it out,
 * `api/files/<type>/file/<client>/<path>`, possibly made absolute by `fullUrl`.
 * The delete endpoint wants `/api/files/<type>/<path>` with the owning client as
 * `clientCode`. Prefixing the whole url instead, as this used to, asked for
 * `/api/files/static/api/files/static/file/...`: the service answered 2xx,
 * deleted nothing, and the file stayed served while the page showed it gone.
 *
 * A value that is already a path under the client root is taken as one.
 */
export function fileDeleteUrl(fileUrl: string, resourceType: string, clientCode?: string): string {
	const bare = fileUrl.replace(/^[a-z]+:\/\/[^/]+/i, '').split('?')[0];
	const m = /(?:^|\/)api\/files\/(?:static|secured)\/(?:file\/([^/]+)\/)?(.*)$/.exec(bare);
	const path = (m ? m[2] : bare).replace(/^\/+/, '').replace(/\/{2,}/g, '/');
	const client = clientCode || m?.[1];
	return `/api/files/${resourceType}/${path}` + (client ? `?clientCode=${client}` : '');
}

/**
 * The file name a FileSelector shows for the value it holds.
 *
 * An upload stores the files service's response, whose `name` is the real name:
 * use it. Otherwise the name is the last segment of the url, which the files
 * service built with Java's `URLEncoder` (form encoding): a space is `+`, a `(`
 * is `%28`, a literal `+` is `%2B`. Shown raw it read `download+%284%29.pdf`.
 * So `+` goes back to a space first, then the percent escapes are decoded. A
 * segment that is not valid percent encoding (a bare `%` in a hand-typed url)
 * is shown as it is rather than throwing.
 */
export function fileSelectorDisplayName(value: any, fileUrl: string): string {
	if (value && typeof value === 'object' && typeof value.name === 'string' && value.name)
		return value.name;
	const segment = (fileUrl ?? '').split('?')[0].split('/').pop() ?? '';
	try {
		return decodeURIComponent(segment.replace(/\+/g, ' '));
	} catch {
		return segment;
	}
}
